import { Component, OnInit } from '@angular/core';
import { MdDialogRef } from '@angular/material';
import { Router } from '@angular/router';
import { ObservableRequestService } from 'app/services/observable-request.service';
import { ConstantsService } from 'app/services/constants-service';
import { SnackBarService } from 'app/services/snackbar.service';
import { EntityMonitoringService } from 'app/entities/monitoring/entity-monitoring.service';

export interface RelatedEntityForTracking {
  entityId: number;
  entityName: string;
  entityType: string;
  isMonitored: boolean;
  linkReasons: string[];
  linkedClientNames: string[];
  selected?: boolean;
  tracking?: boolean;
}

@Component({
  selector: 'app-related-entities-tracking-dialog',
  templateUrl: './related-entities-tracking-dialog.component.html',
  styleUrls: ['./related-entities-tracking-dialog.component.css']
})
export class RelatedEntitiesTrackingDialogComponent implements OnInit {
  entities: RelatedEntityForTracking[] = [];
  filteredEntities: RelatedEntityForTracking[] = [];
  loading = false;
  bulkTracking = false;
  searchTerm = '';
  totalEntities = 0;
  untrackedCount = 0;

  constructor(
    public dialogRef: MdDialogRef<RelatedEntitiesTrackingDialogComponent>,
    private requestService: ObservableRequestService,
    private entityMonitoringService: EntityMonitoringService,
    private snackBarService: SnackBarService,
    private router: Router
  ) {}

  ngOnInit() {
    this.loadEntities();
  }

  loadEntities() {
    this.loading = true;
    this.requestService.getRequest(ConstantsService.MONITORING_RELATED_ENTITIES_FOR_TRACKING).subscribe(
      (response: any) => {
        this.loading = false;
        const result = response && response.arrResult ? response.arrResult : response;
        if (result && result.error) {
          this.entities = [];
          this.filteredEntities = [];
          this.totalEntities = 0;
          this.untrackedCount = 0;
          this.snackBarService.showSimpleMsj('Error loading related entities: ' + result.error);
          return;
        }
        const list = (result && Array.isArray(result.entities))
          ? result.entities
          : (Array.isArray(result) ? result : []);
        this.entities = list.map((e: any) => ({
          entityId: e.entityId,
          entityName: e.entityName || ('Entity ' + e.entityId),
          entityType: e.entityType || 'Unknown',
          isMonitored: !!e.isMonitored,
          linkReasons: Array.isArray(e.linkReasons) ? e.linkReasons : [],
          linkedClientNames: Array.isArray(e.linkedClientNames) ? e.linkedClientNames : [],
          selected: false,
          tracking: false
        }));
        this.totalEntities = result && result.totalEntities != null
          ? result.totalEntities
          : this.entities.length;
        this.untrackedCount = result && result.untrackedCount != null
          ? result.untrackedCount
          : this.entities.filter(e => !e.isMonitored).length;
        this.applyFilter();
        if (this.entities.length === 0) {
          this.snackBarService.showSimpleMsj(
            'No active clients with linked entities found for tracking coverage.');
        }
      },
      (error: any) => {
        this.loading = false;
        this.entities = [];
        this.filteredEntities = [];
        this.snackBarService.showSimpleMsj(
          'Error loading related entities: ' + (error.message || 'Unknown error'));
      }
    );
  }

  onSearchChange(value: string) {
    this.searchTerm = value || '';
    this.applyFilter();
  }

  applyFilter() {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      this.filteredEntities = this.entities.slice();
      return;
    }
    this.filteredEntities = this.entities.filter(e => {
      const nameMatch = e.entityName && e.entityName.toLowerCase().indexOf(term) >= 0;
      const typeMatch = e.entityType && e.entityType.toLowerCase().indexOf(term) >= 0;
      const clientMatch = e.linkedClientNames.some(
        c => c && c.toLowerCase().indexOf(term) >= 0);
      const reasonMatch = e.linkReasons.some(
        r => r && r.toLowerCase().indexOf(term) >= 0);
      return nameMatch || typeMatch || clientMatch || reasonMatch;
    });
  }

  get selectedUntracked(): RelatedEntityForTracking[] {
    return this.filteredEntities.filter(e => e.selected && !e.isMonitored && !e.tracking);
  }

  get filterableUntracked(): RelatedEntityForTracking[] {
    return this.filteredEntities.filter(e => !e.isMonitored);
  }

  get allUntrackedSelected(): boolean {
    const untracked = this.filterableUntracked;
    return untracked.length > 0 && untracked.every(e => e.selected);
  }

  get someUntrackedSelected(): boolean {
    const untracked = this.filterableUntracked;
    const selectedCount = untracked.filter(e => e.selected).length;
    return selectedCount > 0 && selectedCount < untracked.length;
  }

  toggleSelectAll(checked: boolean) {
    this.filteredEntities.forEach(e => {
      if (!e.isMonitored) {
        e.selected = checked;
      }
    });
  }

  trackEntity(entity: RelatedEntityForTracking) {
    if (!entity || entity.isMonitored || entity.tracking) {
      return;
    }
    entity.tracking = true;
    this.entityMonitoringService.track(entity.entityId).subscribe(
      () => {
        entity.tracking = false;
        entity.isMonitored = true;
        entity.selected = false;
        this.untrackedCount = this.entities.filter(e => !e.isMonitored).length;
        this.snackBarService.showSimpleMsj(
          entity.entityName + ' registered for continuous monitoring.');
      },
      (error: any) => {
        entity.tracking = false;
        this.snackBarService.showSimpleMsj(
          'Failed to track ' + entity.entityName + ': ' + (error.message || 'Unknown error'));
      }
    );
  }

  trackSelected() {
    const toTrack = this.selectedUntracked;
    if (toTrack.length === 0) {
      return;
    }
    this.bulkTracking = true;
    let remaining = toTrack.length;
    let successCount = 0;
    let errorCount = 0;

    const doneOne = () => {
      remaining--;
      if (remaining <= 0) {
        this.bulkTracking = false;
        this.untrackedCount = this.entities.filter(e => !e.isMonitored).length;
        let message = 'Tracked ' + successCount + (successCount === 1 ? ' entity' : ' entities') + '.';
        if (errorCount > 0) {
          message += ' ' + errorCount + ' failed.';
        }
        this.snackBarService.showSimpleMsj(message);
      }
    };

    toTrack.forEach(entity => {
      entity.tracking = true;
      this.entityMonitoringService.track(entity.entityId).subscribe(
        () => {
          entity.tracking = false;
          entity.isMonitored = true;
          entity.selected = false;
          successCount++;
          doneOne();
        },
        () => {
          entity.tracking = false;
          errorCount++;
          doneOne();
        }
      );
    });
  }

  openMonitoring(entity: RelatedEntityForTracking) {
    this.dialogRef.close();
    this.router.navigate(['/entity', entity.entityId, 'monitoring']);
  }

  close() {
    this.dialogRef.close();
  }

  linkReasonsLabel(entity: RelatedEntityForTracking): string {
    return (entity.linkReasons || []).join(', ');
  }

  linkedClientsLabel(entity: RelatedEntityForTracking): string {
    return (entity.linkedClientNames || []).join(', ');
  }
}



// WEBPACK FOOTER //
// ./src/app/entities/monitoring/pending-hits-overview/related-entities-tracking-dialog/related-entities-tracking-dialog.component.ts